import { useEffect, useState } from "react";
import { LuLoaderCircle, LuRefreshCw, LuX } from "react-icons/lu";
import { colors, typography, effects } from "../../../design/tokens";
import { HttpAssistantIsWorkingException } from "../../../model/httpException";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import {
  useIsProcessingRollbackError,
  useRollbackErrorHandlerActions,
} from "../../../view_stores/useRollbackErrorHandlerStore";

export default function ProjectRollbackErrorActions() {
  return (
    <div
      className="h-full w-full overflow-hidden
    flex flex-col items-center justify-start"
    >
      <div className={`border ${effects.borderRadius.xl} border-dark-border p-6 max-w-[600px] w-full`}>
        <div className="flex flex-col items-center gap-6">
          <div className={`${typography.size.base} ${colors.text.primary} leading-[1.6] text-left`}>
            Something went wrong while processing your revert request. Would you like
            to try again, or abort and use a different checkpoint?
          </div>
          <Actions />
        </div>
      </div>
    </div>
  );
}

function Actions() {
  const isProcessingRollbackError = useIsProcessingRollbackError();
  const { showSnackbar } = useSnackbar();

  const [selectedActionIndex, setSelectedActionIndex] = useState<number | null>(
    null
  );
  const { abort, retry } = useRollbackErrorHandlerActions();

  const handleAbort = async () => {
    setSelectedActionIndex(0);
    try {
      await abort();
    } catch (error) {
      setSelectedActionIndex(null);
      if (error instanceof HttpAssistantIsWorkingException) {
        showSnackbar(
          "Previous action is still in progress, try again later.",
          {
            type: "error",
            duration: 5000,
          }
        );
      } else {
        throw error;
      }
    }
  };

  const handleRetry = async () => {
    setSelectedActionIndex(1);
    try {
      await retry();
    } catch (error) {
      setSelectedActionIndex(null);
      if (error instanceof HttpAssistantIsWorkingException) {
        showSnackbar(
          "Previous action is still in progress, try again later.",
          {
            type: "error",
            duration: 5000,
          }
        );
      } else {
        throw error;
      }
    }
  };

  useEffect(() => {
    if (!isProcessingRollbackError && selectedActionIndex) {
      setSelectedActionIndex(null);
    }
  }, [isProcessingRollbackError, selectedActionIndex]);

  return (
    <div className="w-full flex gap-4">
      <ActionBtn
        id="project-rollback-error-abort"
        label="Abort"
        icon={<LuX />}
        isDisabled={selectedActionIndex !== null}
        isLoading={selectedActionIndex === 0}
        bgColor="bg-dark-surface-secondary"
        textColor={colors.text.secondary}
        onClick={handleAbort}
      />

      <ActionBtn
        id="project-rollback-error-retry"
        label="Retry"
        icon={<LuRefreshCw />}
        isDisabled={selectedActionIndex !== null}
        isLoading={selectedActionIndex === 1}
        bgColor="bg-[#16A34A]"
        textColor="text-white"
        onClick={handleRetry}
      />
    </div>
  );
}

function ActionBtn({
  id,
  label,
  icon,
  isDisabled,
  isLoading,
  bgColor,
  textColor,
  onClick,
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  isDisabled: boolean;
  isLoading: boolean;
  bgColor: string;
  textColor: string;
  onClick: () => void;
}) {
  return (
    <div
      className={`w-full ${effects.borderRadius.md}
    relative overflow-hidden`}
    >
      <button
        id={id}
        onClick={onClick}
        className={`w-full flex items-center justify-center
    gap-2 py-2
    ${colors.hover.brightness} hover:cursor-pointer
    ${effects.transition.default} ${typography.size.sm}
    ${bgColor} ${textColor}`}
        disabled={isDisabled}
      >
        {icon}
        {label}
      </button>
      {isLoading && (
        <div
          className="absolute top-0 bg-black/50 w-full h-full
          flex items-center justify-center"
        >
          <LuLoaderCircle className="animate-spin text-white" size={24} />
        </div>
      )}
    </div>
  );
}
